import { createContext, useContext, useState, useEffect } from 'react'
import type { ComponentType, ReactNode } from 'react'


import { encode, decode } from 'js-base64'
import _ from 'lodash'
import { getProviders, getSession, signIn } from '@/shared/services/session/compat'
import { useCookies } from 'react-cookie'
import { useIsClient } from 'usehooks-ts'


import { env } from '@/env'
import { useAppRouter as useRouter } from '@/shared/hooks/useAppRouter'

// import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'

import { useAuthActions } from '@/shared/lib/auth/actions'
import { showErrorAlert } from '@/shared/lib/errors'
import {
  authPaths,
  toRelativeCallbackPath
} from '@/shared/lib/routing/auth'
import { BlockUI } from '@/shared/ui/BlockUI'
import { alert, closeDialog, showActiveSessionDialog } from '@/shared/ui/dialog'

import { shouldAlertAuthQueryError } from '@/features/auth/pages/shared'

import {
  AUTH_ACTIONS,
  EMAIL_COOKIE_KEY,
  PASSWORD_COOKIE_KEY
} from './constants'

import type {
  AuthAction,
  AuthNavigationOptions,
  AuthContextValue,
  AuthProviderConfig,
  AuthStyleConfig
} from './types'

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Acesso ao contexto de autenticação.
 *
 * O retorno é não-nulo por asserção: todo consumidor está sob `AuthProvider` —
 * as páginas de auth são embrulhadas por `withAuth`. Um consumidor fora do
 * provider é erro de montagem, não estado a ser tratado em cada chamada.
 */
const useAuthContext = () => useContext(AuthContext) as AuthContextValue

const [
  SIGN_IN,
  SIGN_UP,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
  CONFIRMATION,
  RESEND_CONFIRMATION
] = AUTH_ACTIONS
const DEFAULT_STYLE_CONFIG: AuthStyleConfig = {
  size: 'lg',
  solid: false,
  shadow: false,
  transparent: false
}

const getStyleConfig = (userStyleConfig?: AuthStyleConfig): AuthStyleConfig => {
  const newConfig: AuthStyleConfig = { ...DEFAULT_STYLE_CONFIG, ...userStyleConfig }

  newConfig.style = 'outline'
  newConfig.btnStyle = 'outline'

  if (newConfig.solid) {
    newConfig.style = 'solid'
    newConfig.btnStyle = 'light'
  } else if (newConfig.shadow) {
    newConfig.style = 'shadow'
    newConfig.btnStyle = 'shadow'
  } else if (newConfig.transparent) {
    newConfig.style = 'transparent'
    newConfig.btnStyle = 'transparent'
  }

  return newConfig
}

type AuthProviderProps = Partial<AuthProviderConfig> & {
  checkSessionOnAuthPages?: boolean
  reCaptchaVersion?: 'v2' | 'v3'
  reCaptchaKey?: string
  /** Aceita `true` para desligar sempre, ou a lista de ações em que desliga. */
  disableReCaptcha?: boolean | AuthAction[]
  callbackUrl?: string
  children?: ReactNode
  [key: string]: unknown
}

const AuthProvider = ({
  afterSignInRedirectUrl = '/',
  httpClient,
  disableReCaptcha = false,
  reCaptchaVersion = 'v3',
  reCaptchaKey = env.NEXT_PUBLIC_EVNTTZ_RECAPTCHA_KEY,
  styleConfig: userStyleConfig,
  enabledProviders = [],
  callbackUrl: propsCallbackUrl,
  children,
  ...rest
}: AuthProviderProps) => {
  const router = useRouter()
  const authActions = useAuthActions()
  const {
    callbackUrl: callbackUrlQuery,
    email: emailQuery,
    digitalProductId: digitalProductIdQuery
  } = router.query
  const digitalProductId =
    Array.isArray(digitalProductIdQuery) && digitalProductIdQuery.length
      ? digitalProductIdQuery[0]
      : digitalProductIdQuery
  let callbackUrl = callbackUrlQuery || afterSignInRedirectUrl
  if (propsCallbackUrl) callbackUrl = propsCallbackUrl
  callbackUrl = toRelativeCallbackPath(
    callbackUrl != null ? String(callbackUrl) : undefined
  )
  const [isLoading, setIsLoading] = useState(false)
  const [providers, setProviders] = useState({})
  // A busca é assíncrona: sem esta marca, quem depende de "existe provedor
  // social?" lê `false` no primeiro quadro e muda de ideia quando a resposta
  // chega — foi o que fazia a tela de cadastro piscar.
  const [providersLoaded, setProvidersLoaded] = useState(false)
  const [_filled, setFilled] = useState(false)

  const isClient = useIsClient()

  const getDefaultValues = ({ ignoreFilled = false } = {}) => {
    const defaultValues = { email: currentEmail }

    if (ignoreFilled) return defaultValues

    if (_filled) {
      setFilled(false)

      return defaultValues
    }

    return {}
  }

  const [cookies, setCookie, removeCookie] = useCookies([
    EMAIL_COOKIE_KEY,
    PASSWORD_COOKIE_KEY
  ])
  const currentEmail = cookies[EMAIL_COOKIE_KEY]
  const currentPassword =
    cookies[PASSWORD_COOKIE_KEY] && decode(cookies[PASSWORD_COOKIE_KEY])

  const setCurrentEmail = email => {
    if (
      typeof email != 'undefined' &&
      email != 'undefined' &&
      email !== currentEmail
    )
      setCookie(EMAIL_COOKIE_KEY, email, { path: '/' })
  }
  const setCurrentPassword = password =>
    password && setCookie(PASSWORD_COOKIE_KEY, encode(password))

  const styleConfig = getStyleConfig(userStyleConfig)
  // `disableReCaptcha` aceitava booleano ou lista de ações. A lista dependia da
  // máquina de estado que saiu com o modal; o `config` sempre passou booleano.
  const reCaptchaEnabled = !!reCaptchaKey && !disableReCaptcha
  const useReCaptchaV3 = reCaptchaEnabled && reCaptchaVersion == 'v3'
  const useReCaptchaV2 = reCaptchaEnabled && reCaptchaVersion == 'v2'

  const goToSignIn = (props: AuthNavigationOptions & { filled?: boolean } = {}) => {
    const { filled } = props
    if (filled) setFilled(filled)

    router.push(authPaths.signIn({ callbackUrl }))
  }
  const goToSignUp = () => {

    router.push(authPaths.signUp({ callbackUrl }))
  }
  const goToForgotPassword = (props: AuthNavigationOptions = {}) => {
    const { email } = props
    setCurrentEmail(email)
    setFilled(true)

    router.push(authPaths.forgotPassword())
  }
  const goToResetPassword = (props: AuthNavigationOptions = {}) => {
    const { email } = props
    setCurrentEmail(email)

    router.push(authPaths.resetPassword())
  }
  const goToConfirmation = ({
    email,
    password = ''
  }: AuthNavigationOptions & { password?: string }) => {
    setCurrentEmail(email)
    setCurrentPassword(password)

    router.push(authPaths.confirmation({ email }))
  }
  const goToResendConfirmation = () => {

    router.push(authPaths.resendConfirmation())
  }
  const handleSignIn = async ({ login, password }) => {
    setIsLoading(true)

    const { error, ok, status, url } = await signIn('domain-sign-in', {
      login: login,
      password: password,
      callbackUrl,
      redirect: false
    })

    setIsLoading(false)

    return { error, ok, status, url }
  }
  const onRequestPasswordReset = async data => {
    setIsLoading(true)
    const { email } = data

    setCurrentEmail(email)

    httpClient
      .post('/auth/reset-password', {
        email
      })
      .then(() => goToResetPassword({ email }))
      .catch(showErrorAlert)
      .finally(() => setIsLoading(false))
  }
  const onRequestConfirmationToken = async data => {
    setIsLoading(true)
    const { email } = data

    setCurrentEmail(email)

    httpClient
      .post('/auth/request-confirmation-token', {
        email
      })
      .then(() => goToConfirmation({ email }))
      .catch(showErrorAlert)
      .finally(() => setIsLoading(false))
  }


  const checkError = () => {
    const { error: errorCode } = router.query

    // NoTicket tem `AuthNoTicketView` — o alertdialog genérico "Oops..." só
    // atrapalha e mascara a tela de ciência de que a pessoa não tem ingresso.
    if (!shouldAlertAuthQueryError(errorCode)) return

    void alert({
      variant: 'destructive',
      title: 'Oops...',
      description: String(
        Array.isArray(errorCode) ? errorCode[0] : errorCode
      )
    })
  }

  const loadProviders = async () => {
    try {
      if (!isClient) return
      if (enabledProviders && enabledProviders.length) {
        const all = (await getProviders()) ?? {}
        const filtered = _.pick(all, enabledProviders)
        setProviders(filtered)
      } else {
        setProviders({})
      }
    } catch {
      setProviders({})
    } finally {
      setProvidersLoaded(true)
    }
  }

  useEffect(() => {
    loadProviders()
  }, [isClient, enabledProviders])

  // Reage à query: montagem com `error=NoTicket` e navegação client-side que
  // acrescenta o param precisam passar pelo mesmo skip.
  useEffect(() => {
    checkError()
  }, [router.query?.error])

  // Se algum fluxo enfileirou "Oops... NoTicket" antes do skip, descarta ao
  // entrar na tela dedicada.
  useEffect(() => {
    if (!shouldAlertAuthQueryError(router.query?.error) && router.query?.error) {
      closeDialog(false)
    }
  }, [router.query?.error])

  useEffect(() => {
    if (emailQuery && currentEmail !== emailQuery) setCurrentEmail(emailQuery)
  }, [emailQuery])

  return (
    <AuthContext.Provider
      value={{
        afterSignInRedirectUrl,
        providers,
        providersLoaded,
        httpClient,
        isLoading,
        setIsLoading,
        styleConfig,

        callbackUrl,

        getDefaultValues,

        currentEmail,
        currentPassword,

        goToSignIn,
        goToSignUp,
        goToForgotPassword,
        goToResetPassword,
        goToConfirmation,
        goToResendConfirmation,

        setCurrentEmail,
        setCurrentPassword,

        reCaptchaEnabled,
        useReCaptchaV3,
        useReCaptchaV2,
        reCaptchaKey,

        handleSignIn,
        onRequestPasswordReset,
        onRequestConfirmationToken,


        ...rest
      }}
    >
      {/* <ConditionalWrapper
        condition={useReCaptchaV3}
        wrapper={children => (
          <GoogleReCaptchaProvider
            reCaptchaKey={env.NEXT_PUBLIC_EVNTTZ_RECAPTCHA_KEY}
            language="PT_BR"
          >
            {children}
          </GoogleReCaptchaProvider>
        )}
      > */}
      <BlockUI blocking={isLoading} className="flex flex-col h-full">
        {children}
      </BlockUI>
      {/* </ConditionalWrapper> */}
    </AuthContext.Provider>
  )
}

/**
 * Embrulha uma página no `AuthProvider`.
 *
 * O genérico preserva as props do componente embrulhado — sem ele, as páginas
 * perderiam o tipo de `digitalProductId` ao serem embrulhadas, que é
 * justamente o que a migração veio corrigir.
 */
function withAuth<P extends object>(
  Component: ComponentType<P>,
  propConfig:
    | Partial<AuthProviderConfig>
    | ((props: P) => Partial<AuthProviderConfig>)
) {
  const WrappedComponent = (componentProps: P) => {
    const config =
      typeof propConfig === 'function' ? propConfig(componentProps) : propConfig

    return (
      <AuthProvider {...componentProps} {...config}>
        <Component {...componentProps} />
      </AuthProvider>
    )
  }

  WrappedComponent.displayName = `withAuth(${
    Component.displayName || Component.name
  })`

  return WrappedComponent
}

export { useAuthContext, AuthProvider, withAuth }
